/**
 * "Widget usage limits" — the dashboard card (owner decision 2026-09-28,
 * migration 089).
 *
 * Every site has per-hour and per-day ceilings on what its widget can do
 * (server/src/services/widget-site-budget.ts): they stop a copied site key
 * from filing unlimited leads or running unlimited AI, and they sit far above
 * normal traffic. Past one, the widget tells visitors it is busy until the
 * window resets. This card shows each against its limit, and lets an owner or
 * admin ask FinderlyFix for more; an operator approves (the new limits apply
 * within a minute) or declines with a reason, which shows here.
 *
 * Accessibility: each budget is a text row ("12 of 60") with the bar
 * `aria-hidden` — the numbers ARE the accessible content, so a screen reader
 * gets them without a meter role's inconsistent announcements. The request
 * form is the kit's <dialog> Modal (focus trap, Esc) with labelled number
 * fields.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import type { WidgetLimitKind, WidgetLimitUsage, WidgetLimitValues, WidgetUsageLimits as Limits } from '../api/types';
import { ErrorNotice, Field, Loading, Modal, Time, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

const nf = new Intl.NumberFormat('en-US');
const NEAR = 0.8;

function fillClass(u: WidgetLimitUsage): string {
  if (u.used === null || u.limit <= 0) return 'fill';
  const r = u.used / u.limit;
  return r >= 1 ? 'fill full' : r >= NEAR ? 'fill near' : 'fill';
}

function requestedWords(values: WidgetLimitValues, limits: WidgetLimitUsage[]): string {
  return limits
    .filter((l) => typeof values[l.kind] === 'number')
    .map((l) => `${l.label.toLowerCase()}: ${nf.format(values[l.kind] as number)}`)
    .join(', ');
}

export function WidgetUsageLimits({ siteKey }: { siteKey: string }) {
  const { api, role } = useSession();
  const toast = useToast();
  const data = useQuery(() => api.widgetUsageLimits(siteKey), [siteKey], { pollMs: 60_000 });
  const [asking, setAsking] = useState(false);
  const withdraw = useMutation(async () => {
    await api.cancelWidgetLimitRequest(siteKey);
    return true;
  });
  const mayRequest = can(role, 'requestLimits');
  const d = data.data;

  return (
    <section className="panel" aria-labelledby="usage-limits-h">
      <div className="panel-head">
        <h2 id="usage-limits-h">Widget usage limits</h2>
        {d ? <span className="small muted">{d.label}</span> : null}
      </div>
      {data.loading && !d ? (
        <Loading label="Loading widget usage limits" />
      ) : data.error ? (
        <div className="panel-body">
          <ErrorNotice error={data.error} title="Could not load the usage limits" />
        </div>
      ) : d ? (
        <div className="panel-body stack">
          <p className="small muted" style={{ margin: 0 }}>
            Ceilings that stop a copied key being used against you. Past one, your widget asks visitors to try again when it resets.
          </p>
          <ul className="usage-limits">
            {d.limits.map((u) => (
              <li key={u.kind}>
                <span>
                  {u.label}
                  {u.overridden ? <span className="tag ok" style={{ marginLeft: 6 }}>Raised for you</span> : null}
                </span>
                <span className="num">
                  {u.used === null ? 'Not available' : `${nf.format(u.used)} of ${nf.format(u.limit)}`}
                  {u.used !== null && u.used >= u.limit ? <span className="visually-hidden"> (limit reached)</span> : null}
                </span>
                <span className="track" aria-hidden="true">
                  <span className={fillClass(u)} style={{ width: `${u.used === null ? 0 : Math.min(100, (u.used / Math.max(1, u.limit)) * 100)}%` }} />
                </span>
              </li>
            ))}
          </ul>
          {d.pending_request ? (
            <div className="notice" role="status">
              <div className="stack-sm" style={{ gap: 4 }}>
                <span>
                  Increase requested <Time iso={d.pending_request.created_at} /> ({requestedWords(d.pending_request.requested, d.limits)}). FinderlyFix
                  is reviewing it.
                </span>
                {mayRequest ? (
                  <span>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={withdraw.pending}
                      onClick={async () => {
                        if (!window.confirm('Withdraw your request for higher limits?')) return;
                        if (await withdraw.run()) {
                          toast('Request withdrawn');
                          await data.reload();
                        }
                      }}
                    >
                      Withdraw request
                    </button>
                  </span>
                ) : null}
              </div>
            </div>
          ) : d.last_decision?.status === 'declined' ? (
            <div className="notice warn">
              Your last request was declined <Time iso={d.last_decision.decided_at} />
              {d.last_decision.decision_reason ? `: “${d.last_decision.decision_reason}”` : '.'}
            </div>
          ) : d.last_decision?.status === 'approved' ? (
            <p className="small muted" style={{ margin: 0 }}>
              Raised by FinderlyFix <Time iso={d.last_decision.decided_at} />.
            </p>
          ) : null}
          <ErrorNotice error={withdraw.error} />
          {mayRequest && !d.pending_request ? (
            <div>
              <button type="button" className="btn" onClick={() => setAsking(true)}>
                Request an increase
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      {asking && d ? (
        <RequestIncreaseDialog
          siteKey={siteKey}
          limits={d}
          onClose={() => setAsking(false)}
          onDone={(next) => {
            setAsking(false);
            toast('Request sent to FinderlyFix');
            data.setData(next);
          }}
        />
      ) : null}
    </section>
  );
}

function RequestIncreaseDialog({
  siteKey,
  limits,
  onClose,
  onDone,
}: {
  siteKey: string;
  limits: Limits;
  onClose: () => void;
  onDone: (next: Limits) => void;
}) {
  const { api } = useSession();
  const [values, setValues] = useState<Partial<Record<WidgetLimitKind, string>>>({});
  const [note, setNote] = useState('');
  const [issue, setIssue] = useState<string | null>(null);
  const send = useMutation((asks: WidgetLimitValues) => api.requestWidgetLimits(siteKey, asks, note));
  useEffect(() => setIssue(null), [values]);

  const asks = (): WidgetLimitValues | string => {
    const out: WidgetLimitValues = {};
    for (const l of limits.limits) {
      const raw = (values[l.kind] ?? '').trim();
      if (!raw) continue;
      const n = Number(raw);
      if (!Number.isInteger(n) || n <= l.limit) return `${l.label}: enter a whole number above ${nf.format(l.limit)}.`;
      out[l.kind] = n;
    }
    return Object.keys(out).length ? out : 'Enter at least one new limit.';
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    const a = asks();
    if (typeof a === 'string') {
      setIssue(a);
      return;
    }
    const next = await send.run(a);
    if (next) onDone(next);
  };

  return (
    <Modal
      open
      title="Request higher usage limits"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={send.pending}>
            Cancel
          </button>
          <button type="submit" form="limit-request-form" className="btn btn-primary" disabled={send.pending}>
            {send.pending ? 'Sending…' : 'Send request'}
          </button>
        </>
      }
    >
      <form id="limit-request-form" className="stack" onSubmit={(e) => void submit(e)} noValidate>
        <p style={{ margin: 0 }}>
          Fill in only the limits you need raised. FinderlyFix reviews every request and replies here; approved limits apply within a minute.
        </p>
        <div className="limit-grid">
          {limits.limits.map((l) => (
            <Field key={l.kind} label={l.label} hint={`Now ${nf.format(l.limit)}`} optional>
              {(p) => (
                <input
                  {...p}
                  className="input"
                  type="number"
                  inputMode="numeric"
                  min={l.limit + 1}
                  step={1}
                  value={values[l.kind] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [l.kind]: e.target.value }))}
                />
              )}
            </Field>
          ))}
        </div>
        <Field label="Why do you need more?" hint="A launch, a campaign, a new region. Up to 500 characters." optional>
          {(p) => <textarea {...p} className="input" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
        {issue ? (
          <p className="small" role="alert" style={{ margin: 0, color: 'var(--bad-ink)' }}>
            {issue}
          </p>
        ) : null}
        <ErrorNotice error={send.error} />
      </form>
    </Modal>
  );
}
